/**
 * Globaler Fokus-Guard – behebt den iPad/Mobile-Safari (WebKit) Runtime-Crash.
 *
 * URSACHE (bewiesen via Stacktrace + Radix-Quellcode):
 *   Programmatischer Fokus `element.focus(...)` wirft auf iPad/WebKit einen
 *   unbehandelten Runtime-Error ("Uncaught runtime errors: Script error.",
 *   Stack: `focus@[native code]`). Dominanter Auslöser ist Radix UI `FocusScope`
 *   (@radix-ui/react-focus-scope), dessen `focus()`-Helfer
 *   `element.focus({ preventScroll: true })` aufruft – beim ÖFFNEN (focusFirst),
 *   SCHLIESSEN (unmount auto-focus) UND im Focus-Trap-Listener JEDES Dialogs,
 *   AlertDialogs, Selects, Menüs oder Popovers. Dieser Pfad liegt INNERHALB von
 *   Radix und ist per Komponenten-Prop (onOpenAutoFocus) nicht vollständig
 *   abdeckbar. Zusätzlich betroffen: das native React-`autoFocus`-Prop.
 *
 * FIX:
 *   Native `HTMLElement.prototype.focus` einmalig in try/catch kapseln. Fokus ist
 *   UX, nicht kritisch. Auf Browsern, die NICHT werfen (Desktop, modernes Safari,
 *   Test-Engines), bleibt das Verhalten 1:1 erhalten (Argumente, `this` und
 *   Rückgabewert werden durchgereicht); nur die WebKit-Ausnahme wird geschluckt.
 *   Es wird keine App-Logik, keine Komponente und keine API verändert.
 *
 * @param {object} proto Ziel-Prototype (Default: HTMLElement.prototype).
 * @returns {boolean} true wenn neu installiert, false wenn bereits/ungeeignet.
 */
export function installSafeFocusGuard(proto) {
  if (!proto) return false;
  const nativeFocus = proto.focus;
  if (typeof nativeFocus !== "function" || nativeFocus.__safeGuarded) return false;

  function safeFocus() {
    try {
      return nativeFocus.apply(this, arguments);
    } catch (_e) {
      /* iPad/WebKit kann beim programmatischen Fokus werfen – bewusst ignorieren. */
    }
  }
  safeFocus.__safeGuarded = true;

  try {
    Object.defineProperty(proto, "focus", {
      value: safeFocus,
      writable: true,
      configurable: true,
    });
  } catch (_e) {
    proto.focus = safeFocus;
  }
  return true;
}

// Einmalige Auto-Installation auf den echten DOM-Prototypes (nur im Browser).
// HTMLElement deckt Inputs/Buttons/Dialoge ab; SVGElement schließt die
// iOS-Safari-Restlücke, falls ein programmatischer Fokus auf ein SVG (Icon/
// Badge) läuft – beide nutzen denselben try/catch-Guard.
if (typeof HTMLElement !== "undefined" && HTMLElement.prototype) {
  installSafeFocusGuard(HTMLElement.prototype);
}
if (typeof SVGElement !== "undefined" && SVGElement.prototype) {
  installSafeFocusGuard(SVGElement.prototype);
}
